import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Chip,
  Tooltip,
  Button,
  ButtonGroup,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  useMediaQuery,
  useTheme,
  SvgIcon,
  Tabs,
  Tab,
} from '@mui/material';
import MensureLoader from '../components/atoms/MensureLoader';
import {
  Article,
  ThumbUp,
  Visibility,
  Group,
  SentimentSatisfied,
  TrendingUp,
  Assessment,
  Insights,
  BarChart as BarChartIcon,
  FactCheck,
  Dashboard as DashboardIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Line, Pie, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  Filler,
} from 'chart.js';
import useDashboardStats from '../hooks/data/useDashboardStats';
import useDashboardWordCloud from '../hooks/data/useDashboardWordCloud';
import useDashboardKeywordsCloud from '../hooks/data/useDashboardKeywordsCloud';
import useBriefingKeywords from '../hooks/useBriefingKeywords';
import { useBrand } from '../context/BrandContext';
import WordCloudCard from '../components/organisms/WordCloudCard/WordCloudCard';
import DashboardFiltersDrawer from '../components/organisms/DashboardFiltersDrawer/DashboardFiltersDrawer';
import DateRangePicker from '../components/molecules/DateRangePicker/DateRangePicker';
import BottomSheetHandle from '../components/atoms/BottomSheetHandle';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  Filler
);

const TierIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M12 3L9.5 8h5z M8.5 10L6 15h12l-2.5-5z M5 17L2.5 22h19L19 17z" />
  </SvgIcon>
);

const clampSmartScore = (value) => Math.max(-10, Math.min(10, Number(value || 0)));
const getSmartScorePercent = (value) => ((clampSmartScore(value) + 10) / 20) * 100;
const formatSignedScore = (value) => {
  const numericValue = Number(value || 0);
  return `${numericValue > 0 ? '+' : ''}${numericValue.toFixed(1)}`;
};
const getSmartScoreColor = (value) => {
  const score = Number(value || 0);
  if (score >= 6) return '#2e7d32';
  if (score >= 2) return '#7cb342';
  if (score > -2) return '#f59e0b';
  if (score > -6) return '#ef6c00';
  return '#d32f2f';
};
const SMART_SCORE_STATUS_COLORS = {
  Excelente: '#2e7d32',
  'Muito Bom': '#4caf50',
  Bom: '#7cb342',
  Regular: '#c0ca33',
  Alerta: '#f59e0b',
  Irregular: '#ef6c00',
  Ruim: '#f4511e',
  'Muito Ruim': '#d32f2f',
  Pessimo: '#b71c1c'
};
const KPI_ELEMENT_CHART_COLORS = [
  '#4caf50',
  '#7cb342',
  '#c0ca33',
  '#f59e0b',
  '#ef6c00',
  '#f4511e',
  '#d32f2f',
  '#42a5f5'
];
const DEFAULT_SMART_SCORE_LEGEND = [
  { label: 'Pessimo', min: -10.0, max: -9.1, count: 0 },
  { label: 'Muito Ruim', min: -9.0, max: -6.1, count: 0 },
  { label: 'Ruim', min: -6.0, max: -4.1, count: 0 },
  { label: 'Irregular', min: -4.0, max: -2.1, count: 0 },
  { label: 'Alerta', min: -2.0, max: 2.0, count: 0 },
  { label: 'Regular', min: 2.1, max: 4.0, count: 0 },
  { label: 'Bom', min: 4.1, max: 6.0, count: 0 },
  { label: 'Muito Bom', min: 6.1, max: 9.0, count: 0 },
  { label: 'Excelente', min: 9.1, max: 10.0, count: 0 }
];
const formatSmartScoreRange = ({ min, max }) => `${min > 0 ? '+' : ''}${Number(min).toFixed(1)} a ${max > 0 ? '+' : ''}${Number(max).toFixed(1)}`;
const sortSmartScoreLegend = (legend = []) => [...legend].sort((a, b) => Number(a.min || 0) - Number(b.min || 0));
const formatPercent = (value) => `${Number(value || 0).toLocaleString('pt-BR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1
})}%`;
const DASHBOARD_PERIOD_LABELS = {
  '1d': 'Último dia',
  '3d': 'Últimos 3 dias',
  '7d': 'Últimos 7 dias',
  '30d': 'Últimos 30 dias',
  all: 'Todo o período'
};
const DATE_TYPE_LABELS = {
  published: 'Data de publicação',
  created: 'Data de cadastro'
};
const formatDateLabel = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('pt-BR');
};
const getKpiElementColor = (componentKey, element = {}, fallbackIndex = 0) => {
  const label = String(element.label || '').toLowerCase();

  if (componentKey === 'sentiment') {
    if (label.includes('positivo')) return '#4caf50';
    if (label.includes('neutro')) return '#f59e0b';
    if (label.includes('negativo')) return '#d32f2f';
  }

  if (componentKey === 'exposure') {
    if (label.includes('dedicada')) return '#4caf50';
    if (label.includes('compartilhada')) return '#7cb342';
    if (label.includes('mencao') || label.includes('menção')) return '#f59e0b';
    if (label.includes('nenhuma')) return '#d32f2f';
  }

  if (componentKey === 'highlight' || componentKey === 'spokesperson' || componentKey === 'keyMessage') {
    if (label.includes('sim') || label.includes('identificado') || label.includes('com mensagem')) return '#4caf50';
    if (label.includes('nao') || label.includes('não') || label.includes('nenhuma')) return '#d32f2f';
  }

  if (componentKey === 'reach') {
    if (label.includes('nacional')) return '#4caf50';
    if (label.includes('regional')) return '#7cb342';
    if (label.includes('local')) return '#f59e0b';
    if (label.includes('sem')) return '#d32f2f';
  }

  return KPI_ELEMENT_CHART_COLORS[fallbackIndex % KPI_ELEMENT_CHART_COLORS.length];
};
const getIndicatorScoreColor = (value) => {
  const score = Number(value || 0);
  if (score >= 9.1) return '#2e7d32';
  if (score >= 6.1) return '#4caf50';
  if (score >= 4.1) return '#7cb342';
  if (score >= 2.1) return '#f59e0b';
  return '#d32f2f';
};
const getStatusInsightItems = (statusItem = {}) => {
  const components = Array.isArray(statusItem.components) ? statusItem.components : [];
  const calculated = components.filter(component => Number(component.count || 0) > 0);
  const weak = calculated
    .filter(component => Number(component.averageScore || 0) < 4.1)
    .slice(0, 3);
  const strong = [...calculated]
    .sort((a, b) => Number(b.averageScore || 0) - Number(a.averageScore || 0))
    .filter(component => Number(component.averageScore || 0) >= 6.1)
    .slice(0, 3);

  return { weak, strong };
};

const Dashboard = () => {
  const { activeBrand, activeBrandData, loading: brandLoading } = useBrand();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const initialDashboardLayer = searchParams.get('dashboardLayer') || 'smartscore';

  const [filters, setFilters] = React.useState({
    scope: '',
    category: '',
    uf: '',
    tags: [],
    keyword: '',
    competitor: '__brand__' // Começa focado na nossa marca
  });

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = React.useState(false);
  const [dashboardLayer, setDashboardLayer] = React.useState(initialDashboardLayer);
  const [mobileLayerDrawerOpen, setMobileLayerDrawerOpen] = React.useState(false);
  const dashboardLayerTabs = React.useMemo(() => [
    { value: 'smartscore', label: 'SmartScore', icon: <Assessment sx={{ fontSize: '1.2rem', mr: 0.75, mb: '-2px' }} /> },
    { value: 'editorial', label: 'Inteligencia Editorial', icon: <Insights sx={{ fontSize: '1.2rem', mr: 0.75, mb: '-2px' }} /> },
    { value: 'performance', label: 'Performance de Midia', icon: <BarChartIcon sx={{ fontSize: '1.2rem', mr: 0.75, mb: '-2px' }} /> },
    { value: 'pr-results', label: 'Resultados de PR', icon: <FactCheck sx={{ fontSize: '1.2rem', mr: 0.75, mb: '-2px' }} /> }
  ], []);

  React.useEffect(() => {
    const layer = searchParams.get('dashboardLayer') || 'smartscore';
    setDashboardLayer(currentLayer => currentLayer === layer ? currentLayer : layer);
  }, [searchParams]);

  const handleDashboardLayerChange = (event, value) => {
    setDashboardLayer(value);
    const nextParams = new URLSearchParams(searchParams);
    if (value === 'smartscore') {
      nextParams.delete('dashboardLayer');
    } else {
      nextParams.set('dashboardLayer', value);
    }
    setSearchParams(nextParams);
  };

  const handleMobileDashboardLayerChange = (event, value) => {
    event?.currentTarget?.blur();
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setMobileLayerDrawerOpen(false);
    if (value !== dashboardLayer) {
      setDashboardLayer(value);
      if (typeof window !== 'undefined') {
        const nextUrl = new URL(window.location.href);
        if (value === 'smartscore') nextUrl.searchParams.delete('dashboardLayer');
        else nextUrl.searchParams.set('dashboardLayer', value);
        window.history.replaceState(window.history.state, '', nextUrl);
      }
    }
  };

  const { keywords, loading: loadingKeywords } = useBriefingKeywords();

  const competitorNames = React.useMemo(() => {
    if (!keywords || !keywords.competitor) return [];
    const names = keywords.competitor.map(c => c.name || c.competitor).filter(Boolean);
    return [...new Set(names)];
  }, [keywords]);

  const analysisMode = filters.competitor === '__brand__' ? 'brand' : 
                       (filters.competitor === '' ? 'all' : 'competitors');

  const handleAnalysisModeChange = (e) => {
    const val = e.target.value;
    if (val === 'brand') {
      setFilters(prev => ({ ...prev, competitor: '__brand__', tags: [], keyword: '' }));
    } else if (val === 'all') {
      setFilters(prev => ({ ...prev, competitor: '', tags: [], keyword: '' }));
    } else if (val === 'competitors') {
      setFilters(prev => ({ ...prev, competitor: '__all_competitors__', tags: [], keyword: '' }));
    }
  };


  const {
    stats,
    tagStats,
    collectionStats,
    loading,
    isFetching,
    timePeriod,
    setTimePeriod,
    customDateRange,
    setCustomDateRange,
    tags: availableTags
  } = useDashboardStats({
    initialTimePeriod: '7d',
    filters
  });

  const [wordLimit, setWordLimit] = React.useState(200);
  const [cloudType, setCloudType] = React.useState('words');
  const [wordCloudContext, setWordCloudContext] = React.useState('all');
  const [quickDateType, setQuickDateType] = React.useState(() => localStorage.getItem('dashboard_dateType') || 'published');
  const [dateRangeOpen, setDateRangeOpen] = React.useState(false);

  const wordCloudCompetitorFilter = React.useMemo(() => {
    if (cloudType === 'words' && wordCloudContext === 'competitor' && filters.competitor === '__brand__') {
      return '__all_competitors__';
    }
    return filters.competitor;
  }, [cloudType, wordCloudContext, filters.competitor]);
  
  const { wordCloud, isFetching: loadingWords } = useDashboardWordCloud({ 
    timePeriod, limit: wordLimit, contextFilter: wordCloudContext, 
    tags: filters.tags, competitor: wordCloudCompetitorFilter, keyword: filters.keyword, customDateRange,
    enabled: dashboardLayer === 'editorial'
  });
  
  const keywordCloudCompetitorFilter = React.useMemo(() => {
    if (cloudType === 'competitors' && filters.competitor === '__brand__') {
      return '__all_competitors__';
    }
    if (cloudType === 'share' && filters.competitor === '__brand__') {
      return '';
    }
    return filters.competitor;
  }, [cloudType, filters.competitor]);

  const { keywordsCloud, isFetching: loadingKeywordsCloud } = useDashboardKeywordsCloud({ 
    timePeriod, limit: wordLimit,
    tags: filters.tags, competitor: keywordCloudCompetitorFilter, keyword: filters.keyword, customDateRange,
    enabled: dashboardLayer === 'editorial'
  });
  const dashboardPeriodLabel = React.useMemo(() => {
    if (timePeriod === 'custom' && customDateRange?.startDate && customDateRange?.endDate) {
      const dateTypeLabel = DATE_TYPE_LABELS[customDateRange.dateType || quickDateType || 'published'] || 'Data de publicação';
      return `${formatDateLabel(customDateRange.startDate)} a ${formatDateLabel(customDateRange.endDate)} · ${dateTypeLabel}`;
    }

    return DASHBOARD_PERIOD_LABELS[timePeriod] || 'Período filtrado';
  }, [timePeriod, customDateRange, quickDateType]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const activeAdvancedFilters = React.useMemo(() => {
    const items = [];
    (filters.tags || []).forEach((tagId) => {
      const tag = (tagStats || []).find(item => String(item?._id || item?.id) === String(tagId));
      items.push({ key: `tag-${tagId}`, field: 'tags', value: tagId, label: `Tag: ${tag?.name || 'Selecionada'}` });
    });
    if (filters.keyword) items.push({ key: 'keyword', field: 'keyword', label: `Palavra: ${filters.keyword}` });
    if (filters.scope) items.push({ key: 'scope', field: 'scope', label: `Abrangência: ${filters.scope}` });
    if (filters.category) items.push({ key: 'category', field: 'category', label: `Categoria: ${filters.category}` });
    if (filters.uf) items.push({ key: 'uf', field: 'uf', label: `UF: ${filters.uf}` });
    return items;
  }, [filters.tags, filters.keyword, filters.scope, filters.category, filters.uf, tagStats]);

  const removeAdvancedFilter = (item) => {
    setFilters(prev => ({
      ...prev,
      [item.field]: item.field === 'tags'
        ? (prev.tags || []).filter(tagId => String(tagId) !== String(item.value))
        : ''
    }));
  };

  const clearAdvancedFilters = () => {
    setFilters(prev => ({ ...prev, scope: '', category: '', uf: '', tags: [], keyword: '' }));
  };

  const handleDashboardPeriodChange = (event) => {
    const value = event.target.value;
    if (value === 'custom') {
      setDateRangeOpen(true);
      return;
    }
    setTimePeriod(value);
    setCustomDateRange(null);
  };

  const handleDashboardCustomDateChange = (range) => {
    setDateRangeOpen(false);
    setTimePeriod('custom');
    setCustomDateRange({ ...range, dateType: range.dateType || quickDateType || 'published' });
    if (range.dateType) {
      setQuickDateType(range.dateType);
      localStorage.setItem('dashboard_dateType', range.dateType);
    }
  };

  const totalArticles = stats.totalArticles || 0;
  const articlesNewToday = stats.articlesToday || 0;
  const readRate = stats.readRate || 0;
  const sentimentPositive = stats.sentiment?.positive || 0;
  const sentimentNeutral = stats.sentiment?.neutral || 0;
  const sentimentNegative = stats.sentiment?.negative || 0;
  const totalExposure = stats.exposure?.total || 0;
  const avgExposure = stats.exposure?.average || 0;

  // New Metrics from Backend
  const audienceMonthly = stats.exposure?.monthly || 0;
  const audienceDaily = stats.exposure?.daily || 0;
  const followersTotal = stats.exposure?.followers || 0;
  const socialViewsTotal = stats.exposure?.views || 0;
  const audienceTotal = stats.exposure?.total || (audienceDaily + socialViewsTotal);
  const audienceByPlatform = stats.exposure?.byPlatform || [];
  const visitsCorrespondence = audienceByPlatform
    .filter(item => item.audienceMonthly > 0 || item.audienceDaily > 0)
    .slice(0, 4)
    .map(item => `${item.name}: ${item.audienceDaily.toLocaleString('pt-BR')}/dia`)
    .join(' | ');
  const followersCorrespondence = audienceByPlatform
    .filter(item => item.followers > 0)
    .slice(0, 4)
    .map(item => `${item.name}: ${item.followers.toLocaleString('pt-BR')}`)
    .join(' | ');
  const socialViewsCorrespondence = audienceByPlatform
    .filter(item => item.views > 0)
    .slice(0, 4)
    .map(item => `${item.name}: ${item.views.toLocaleString('pt-BR')}`)
    .join(' | ');
  const valuationTotal = stats.valuation?.total || 0;
  const valuationTotalUsd = stats.valuation?.totalUsd || 0;
  const usdRate = stats.valuation?.usdRate || 0;
  const socialLikes = stats.socialEngagement?.totalLikes || stats.engagement?.totalLikes || 0;
  const socialComments = stats.socialEngagement?.totalComments || stats.engagement?.totalComments || 0;
  const socialShares = stats.socialEngagement?.totalShares || stats.engagement?.totalShares || 0;
  const socialSaves = stats.socialEngagement?.totalSaves || stats.engagement?.totalSaves || 0;
  const socialTotal = stats.socialEngagement?.total || (socialLikes + socialComments + socialShares + socialSaves);
  const tier1Total = stats.tiers?.['Tier 1'] || stats.tiers?.['1'] || 0;
  const tier2Total = stats.tiers?.['Tier 2'] || stats.tiers?.['2'] || 0;
  const tier3Total = stats.tiers?.['Tier 3'] || stats.tiers?.['3'] || 0;
  const smartScoreGlobal = stats.smartScore || {};
  const metricCoverage = stats.coverage || {};
  const smartScoreValue = Number(smartScoreGlobal.score || 0);
  const smartScoreTargetValue = Number(smartScoreGlobal.targetScore || 6.1);
  const smartScorePercent = getSmartScorePercent(smartScoreValue);
  const smartScoreTargetPercent = getSmartScorePercent(smartScoreTargetValue);
  const smartScoreColor = getSmartScoreColor(smartScoreValue);
  const smartScoreEnabled = Boolean(smartScoreGlobal.enabled);
  const smartScoreLegend = Array.isArray(smartScoreGlobal.statusLegend) && smartScoreGlobal.statusLegend.length
    ? sortSmartScoreLegend(smartScoreGlobal.statusLegend)
    : DEFAULT_SMART_SCORE_LEGEND;
  const smartScoreTargetGap = Number(smartScoreGlobal.targetGap || 0);
  const smartScoreTargetBase = Math.max(Math.abs(smartScoreTargetValue), 0.1);
  const smartScoreTargetDeltaPercent = smartScoreEnabled
    ? (Math.abs(smartScoreTargetGap) / smartScoreTargetBase) * 100
    : 0;
  const smartScoreTargetProgressPercent = smartScoreEnabled
    ? Math.max(0, (smartScoreValue / smartScoreTargetBase) * 100)
    : 0;
  const smartScoreStatusTotal = smartScoreLegend.reduce((sum, item) => sum + Number(item.count || 0), 0);
  const smartScoreStatusDonutData = {
    labels: smartScoreLegend.map(item => item.label),
    datasets: [{
      data: smartScoreLegend.map(item => Number(item.count || 0)),
      backgroundColor: smartScoreLegend.map(item => SMART_SCORE_STATUS_COLORS[item.label] || '#90a4ae'),
      borderWidth: 0,
      hoverOffset: 4
    }]
  };
  const smartScoreComponents = Array.isArray(smartScoreGlobal.components)
    ? smartScoreGlobal.components.filter(component => component.count > 0)
    : [];
  const prResults = stats.prResults || {};

  // Charts Data from Backend
  const categoryStats = stats.charts?.byCategory || [];
  const scopeStats = stats.charts?.byScope || [];
  const ufStats = stats.charts?.byUF || [];

  // KPI Cards Data
  const kpiCards = [
    {
      title: 'Total de Artigos',
      value: totalArticles,
      icon: <Article sx={{ fontSize: 40 }} />,
      color: '#1976d2',
    },
    {
      title: 'Tier',
      value: tier1Total + tier2Total + tier3Total,
      icon: <TierIcon sx={{ fontSize: 40 }} />,
      color: '#9e9e9e',
      breakdown: [
        { label: 'Tier 1', value: tier1Total, color: '#4caf50' },
        { label: 'Tier 2', value: tier2Total, color: '#ff9800' },
        { label: 'Tier 3', value: tier3Total, color: '#9e9e9e' },
      ],
    },
    {
      title: 'Audiência',
      value: audienceTotal,
      icon: <Visibility sx={{ fontSize: 40 }} />,
      color: '#3b82f6',
      breakdown: [
        { label: 'Visitas Dia', value: audienceDaily, color: '#0288d1' },
        { label: 'Visualizações', value: socialViewsTotal, color: '#f59e0b' },
      ],
      helper: 'Visitas diárias de Online + visualizações de redes sociais',
    },
    {
      title: 'Visitas Online',
      value: audienceMonthly,
      icon: <Visibility sx={{ fontSize: 40 }} />,
      color: '#a855f7',
      breakdown: [
        { label: 'Mês', value: audienceMonthly, color: '#a855f7' },
        { label: 'Dia', value: audienceDaily, color: '#0288d1' },
      ],
      helper: visitsCorrespondence ? `Correspondência: ${visitsCorrespondence}` : 'Correspondência não informada',
    },
    {
      title: 'Visualizações',
      value: socialViewsTotal,
      icon: <Visibility sx={{ fontSize: 40 }} />,
      color: '#f59e0b',
      breakdown: [
        { label: 'Total', value: socialViewsTotal, color: '#f59e0b' },
      ],
      helper: socialViewsCorrespondence ? `Correspondência: ${socialViewsCorrespondence}` : 'Correspondência não informada',
    },
    {
      title: 'Seguidores',
      value: followersTotal,
      icon: <Group sx={{ fontSize: 40 }} />,
      color: '#06b6d4',
      breakdown: [
        { label: 'Perfis únicos', value: followersTotal, color: '#06b6d4' },
      ],
      helper: followersCorrespondence ? `Correspondência: ${followersCorrespondence}` : 'Correspondência não informada',
    },
    {
      title: 'Redes Sociais',
      value: socialTotal,
      icon: <ThumbUp sx={{ fontSize: 40 }} />,
      color: '#ec4899',
      breakdown: [
        { label: 'Curtidas', value: socialLikes, color: '#ec4899' },
        { label: 'Comentários', value: socialComments, color: '#8b5cf6' },
        { label: 'Compartilh.', value: socialShares, color: '#06b6d4' },
        { label: 'Salvos', value: socialSaves, color: '#22c55e' },
      ],
    },
    {
      title: 'Valoração',
      value: valuationTotal + valuationTotalUsd,
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      color: '#2e7d32',
      breakdown: [
        { label: 'R$', value: valuationTotal, color: '#2e7d32', format: 'currencyBrl' },
        { label: 'US$', value: valuationTotalUsd, color: '#22c55e', format: 'currencyUsd' },
      ],
      helper: usdRate ? `Dólar médio: R$ ${usdRate.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}` : 'Dólar não informado',
    },
    {
      title: 'Sentimento',
      value: sentimentPositive + sentimentNeutral + sentimentNegative,
      icon: <SentimentSatisfied sx={{ fontSize: 40 }} />,
      color: '#9c27b0',
      breakdown: [
        { label: 'Positivo', value: sentimentPositive, color: '#4caf50' },
        { label: 'Neutro', value: sentimentNeutral, color: '#ff9800' },
        { label: 'Negativo', value: sentimentNegative, color: '#f44336' },
      ],
    },
  ];

  // Chart Data - Sentiment Distribution
  const sentimentData = {
    labels: ['Positivo', 'Neutro', 'Negativo'],
    datasets: [{
      data: [
        sentimentPositive,
        sentimentNeutral,
        sentimentNegative,
      ],
      backgroundColor: ['#4caf50', '#ff9800', '#f44336'],
    }],
  };

  const exposureData = {
    labels: ['Dedicada', 'Compartilhada', 'Citação'],
    datasets: [{
      data: [
        stats.charts?.byExposure?.dedicated || 0,
        stats.charts?.byExposure?.shared || 0,
        stats.charts?.byExposure?.mention || 0,
      ],
      backgroundColor: ['#1976d2', '#7b1fa2', '#ff9800'],
    }],
  };

  // Chart Data - Top Tags
  const topTagsData = {
    labels: (tagStats || []).slice(0, 10).map(t => t.name),
    datasets: [{
      label: 'Artigos por Tag',
      data: (tagStats || []).slice(0, 10).map(t => t.count),
      backgroundColor: '#1976d2',
    }],
  };

  // Chart Data - Articles Over Time
  const articlesOverTimeData = {
    labels: stats.timeline?.map(d => new Date(d.date).toLocaleDateString()) || [],
    datasets: [{
      label: 'Artigos por Dia',
      data: stats.timeline?.map(d => d.count) || [],
      borderColor: '#1976d2',
      backgroundColor: 'rgba(25, 118, 210, 0.1)',
      fill: true,
      tension: 0.4,
    }],
  };

  // Chart Data - Category Distribution
  const categoryChartData = {
    labels: categoryStats.map(c => c._id),
    datasets: [{
      data: categoryStats.map(c => c.count),
      backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40']
    }]
  };

  // Chart Data - Scope Distribution
  const scopeChartData = {
    labels: scopeStats.map(s => s._id),
    datasets: [{
      data: scopeStats.map(s => s.count),
      backgroundColor: ['#4caf50', '#2196f3', '#ff9800']
    }]
  };

  // Chart Data - UF Distribution
  const ufChartData = {
    labels: ufStats.map(u => u._id),
    datasets: [{
      label: 'Artigos por Estado',
      data: ufStats.map(u => u.count),
      backgroundColor: '#673ab7'
    }]
  };


  const brandVolume = stats?.charts?.shareOfVoice?.brandCount?.[0]?.count || 0;
  const competitorsData = stats?.charts?.shareOfVoice?.competitorsUnwound || [];
  const competitorsVolume = competitorsData.reduce((sum, c) => sum + c.count, 0);

  const sovPieChartData = {
    labels: [activeBrandData?.name || 'Marca', 'Concorrentes'],
    datasets: [{
      data: [brandVolume, competitorsVolume],
      backgroundColor: ['#2196f3', '#f44336']
    }]
  };

  const sovBarChartData = {
    labels: [activeBrandData?.name || 'Marca', ...competitorsData.map(c => c._id)],
    datasets: [{
      label: 'Volume de Artigos',
      data: [brandVolume, ...competitorsData.map(c => c.count)],
      backgroundColor: ['#2196f3', ...competitorsData.map(() => '#ff9800')]
    }]
  };

  const platformChartData = {
    labels: (stats.platforms || []).map(p => p.name),
    datasets: [{
      label: 'Artigos por Plataforma',
      data: (stats.platforms || []).map(p => p.count),
      backgroundColor: '#0288d1',
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: isMobile ? false : undefined,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.label || context.dataset.label || '';
            const parsedValue = typeof context.parsed === 'object' ? context.parsed?.y : context.parsed;
            const value = parsedValue ?? context.raw ?? 0;
            return label ? `${label}: ${value}` : `${value}`;
          }
        }
      }
    },
  };
  const smartScoreStatusDonutOptions = {
    ...chartOptions,
    cutout: '68%',
    plugins: {
      ...chartOptions.plugins,
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: function (context) {
            const value = Number(context.raw || 0);
            const percent = smartScoreStatusTotal > 0 ? (value / smartScoreStatusTotal) * 100 : 0;
            return `${context.label}: ${value.toLocaleString('pt-BR')} (${formatPercent(percent)})`;
          }
        }
      }
    }
  };

  if (loading || brandLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <MensureLoader size="lg" message="Carregando dashboard..." />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header & Filters */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontSize: { xs: '1.65rem', md: '2.125rem' }, lineHeight: 1.08 }}>
              <DashboardIcon color="primary" sx={{ fontSize: { xs: 30, md: 36 } }} />
              Dashboard - {activeBrandData?.name || 'Carregando...'}
            </Typography>
            <Chip
              size="small"
              label={`Período aplicado: ${dashboardPeriodLabel}`}
              variant="outlined"
              sx={{
                display: { xs: 'none', md: 'inline-flex' },
                mt: 1,
                fontWeight: 700,
                color: 'text.secondary',
                borderColor: 'divider',
                bgcolor: 'action.hover'
              }}
            />
          </Box>
          
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <FormControl sx={{ minWidth: 180 }} size="small">
              <InputLabel>Foco Global</InputLabel>
              <Select
                value={analysisMode}
                onChange={handleAnalysisModeChange}
                label="Foco Global"
              >
                <MenuItem value="brand">{activeBrandData?.name || 'Marca'}</MenuItem>
                <MenuItem value="competitors">Concorrentes</MenuItem>
                <MenuItem value="custom">Personalizado...</MenuItem>
                <MenuItem value="all">Tudo</MenuItem>
              </Select>
            </FormControl>

            {analysisMode === 'competitors' && (
              <FormControl sx={{ minWidth: 180 }} size="small">
                <InputLabel>Concorrente</InputLabel>
                <Select
                  value={filters.competitor === '__all_competitors__' ? '__all_competitors__' : filters.competitor}
                  onChange={(e) => {
                    setFilters(prev => ({ ...prev, competitor: e.target.value, tags: [], keyword: '' }));
                  }}
                  label="Concorrente"
                >
                  <MenuItem value="__all_competitors__">Todos</MenuItem>
                  {competitorNames.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Button 
              variant={activeAdvancedFilters.length > 0 ? "contained" : "outlined"}
              color="primary"
              startIcon={<FilterListIcon />}
              onClick={() => setIsFilterDrawerOpen(true)}
              sx={{ height: '40px', borderRadius: 2 }}
            >
              Filtros{activeAdvancedFilters.length > 0 ? ` (${activeAdvancedFilters.length})` : ''}
            </Button>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Período</InputLabel>
              <Select
                value={timePeriod}
                onChange={handleDashboardPeriodChange}
                label="Período"
              >
                <MenuItem value="1d">Último dia</MenuItem>
                <MenuItem value="3d">Últimos 3 dias</MenuItem>
                <MenuItem value="7d">Últimos 7 dias</MenuItem>
                <MenuItem value="30d">Últimos 30 dias</MenuItem>
                <MenuItem
                  value="custom"
                  onClick={() => {
                    setTimeout(() => setDateRangeOpen(true), 0);
                  }}
                >
                  Personalizado...
                </MenuItem>
                <MenuItem value="all">Tudo</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1, mt: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', fontWeight: 700, textTransform: 'uppercase' }}>
              Contexto aplicado
            </Typography>
            <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: 750 }}>
              {analysisMode === 'brand' ? (activeBrandData?.name || 'Marca') : analysisMode === 'competitors' ? 'Concorrentes' : 'Tudo'} · {dashboardPeriodLabel}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<FilterListIcon />}
            onClick={() => setIsFilterDrawerOpen(true)}
            sx={{ minHeight: 42, px: 2, borderRadius: 2, textTransform: 'none', fontWeight: 750, flexShrink: 0 }}
          >
            Filtros{activeAdvancedFilters.length > 0 ? ` (${activeAdvancedFilters.length})` : ''}
          </Button>
        </Box>

        {activeAdvancedFilters.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.5, overflowX: 'auto', pb: 0.5, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 750, color: 'text.secondary', whiteSpace: 'nowrap' }}>
              Filtros ativos:
            </Typography>
            {activeAdvancedFilters.map(item => (
              <Chip
                key={item.key}
                size="small"
                label={item.label}
                onDelete={() => removeAdvancedFilter(item)}
                color="primary"
                variant="outlined"
                sx={{ flexShrink: 0, fontWeight: 650 }}
              />
            ))}
            <Button size="small" color="inherit" onClick={clearAdvancedFilters} sx={{ flexShrink: 0, minWidth: 'auto', textTransform: 'none', fontSize: '0.7rem' }}>
              Limpar tudo
            </Button>
          </Box>
        )}
      </Box>

      {/* Modern Filter Drawer Component  */}
      <DashboardFiltersDrawer 
        open={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        filters={filters}
        setFilters={setFilters}
        brandName={activeBrandData?.name}
        competitorNames={competitorNames}
        availableTags={tagStats || []}
        availableKeywords={keywordsCloud}
        analysisMode={analysisMode}
        onAnalysisModeChange={handleAnalysisModeChange}
        timePeriod={timePeriod}
        onTimePeriodChange={handleDashboardPeriodChange}
        onOpenCustomPeriod={() => setDateRangeOpen(true)}
      />

      {dateRangeOpen && (
        <DateRangePicker
          value={customDateRange}
          onChange={handleDashboardCustomDateChange}
          onClose={() => setDateRangeOpen(false)}
          size="small"
          label="PerÃ­odo Personalizado"
        />
      )}

      {isFetching && (
        <LinearProgress sx={{ mb: 2 }} />
      )}

      <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
        {(() => {
          const activeTab = dashboardLayerTabs.find(tab => tab.value === dashboardLayer) || dashboardLayerTabs[0];
          return (
            <Button
              fullWidth
              variant="outlined"
              onClick={(event) => {
                event.currentTarget.blur();
                setMobileLayerDrawerOpen(true);
              }}
              endIcon={<ExpandMoreIcon />}
              sx={{ minHeight: 46, justifyContent: 'space-between', px: 1.5, textTransform: 'none', fontWeight: 750 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>{activeTab.icon}{activeTab.label}</Box>
            </Button>
          );
        })()}
      </Box>

      <Drawer
        anchor="bottom"
        open={mobileLayerDrawerOpen}
        onClose={() => setMobileLayerDrawerOpen(false)}
        transitionDuration={0}
        ModalProps={{
          disableRestoreFocus: true
        }}
        PaperProps={{ sx: { borderRadius: '18px 18px 0 0', px: 2, pt: 1, pb: 'calc(20px + env(safe-area-inset-bottom, 0px))' } }}
      >
        <BottomSheetHandle sx={{ mb: 1.5 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box>
            <Typography sx={{ fontSize: '1rem', fontWeight: 850 }}>Área do Dashboard</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Escolha quais resultados deseja visualizar</Typography>
          </Box>
          <IconButton onClick={() => setMobileLayerDrawerOpen(false)} aria-label="Fechar áreas do Dashboard"><CloseIcon /></IconButton>
        </Box>
        <List disablePadding>
          {dashboardLayerTabs.map(tab => {
            const selected = dashboardLayer === tab.value;
            return (
              <ListItemButton
                key={tab.value}
                selected={selected}
                onClick={(event) => handleMobileDashboardLayerChange(event, tab.value)}
                sx={{ minHeight: 50, borderRadius: 2, mb: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: selected ? 'primary.main' : 'text.secondary' }}>{tab.icon}</ListItemIcon>
                <ListItemText primary={tab.label} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: selected ? 800 : 600 }} />
                {selected && <CheckIcon color="primary" />}
              </ListItemButton>
            );
          })}
        </List>
      </Drawer>

      <Box sx={{ display: { xs: 'none', md: 'block' }, mb: 3, overflowX: 'auto', pb: 0.5 }}>
        <ButtonGroup
          size="medium"
          variant="outlined"
          sx={{
            borderRadius: 2,
            minWidth: { xs: 760, md: 'auto' },
            '& .MuiButtonGroup-grouped': {
              borderColor: 'divider'
            }
          }}
        >
          {dashboardLayerTabs.map((tab) => {
            const isActive = dashboardLayer === tab.value;
            return (
              <Button
                key={tab.value}
                variant={isActive ? 'contained' : 'outlined'}
                onClick={(event) => handleDashboardLayerChange(event, tab.value)}
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: 0.75,
                  minWidth: { xs: 170, sm: 'auto' },
                  fontWeight: isActive ? 700 : 500,
                  textTransform: 'none',
                  whiteSpace: 'nowrap'
                }}
              >
                {tab.icon}
                {tab.label}
              </Button>
            );
          })}
        </ButtonGroup>
      </Box>

      {dashboardLayer === 'smartscore' && (
      <>
      {/* SmartScore global da marca */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: { xs: 2, md: 3 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4} lg={3}>
            <Typography variant="overline" color="text.secondary" fontWeight="bold">
              SmartScore da Marca
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="h3" fontWeight="bold" sx={{ color: smartScoreEnabled ? smartScoreColor : 'text.disabled' }}>
                {smartScoreEnabled ? formatSignedScore(smartScoreValue) : '--'}
              </Typography>
              <Chip
                size="small"
                label={smartScoreEnabled ? smartScoreGlobal.status : 'Sem dados'}
                sx={{
                  fontWeight: 700,
                  color: smartScoreEnabled ? smartScoreColor : 'text.secondary',
                  bgcolor: smartScoreEnabled ? `${smartScoreColor}1f` : 'action.hover'
                }}
              />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 0.75, alignItems: 'center', mt: 1.2, maxWidth: 300 }}>
              <Chip
                size="small"
                label={`Meta: ${formatSignedScore(smartScoreTargetValue)} - ${smartScoreGlobal.targetStatus || 'Muito Bom'}`}
                variant="outlined"
                sx={{
                  fontWeight: 700,
                  justifyContent: 'flex-start',
                  maxWidth: '100%',
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              />
              <Chip
                size="small"
                label={smartScoreEnabled
                  ? `Distancia: ${smartScoreGlobal.targetReached ? 'superou' : 'faltam'} ${Math.abs(Number(smartScoreGlobal.targetGap || 0)).toFixed(1)}`
                  : 'Aguardando score'}
                color={smartScoreGlobal.targetReached ? 'success' : 'warning'}
                variant="outlined"
                sx={{
                  fontWeight: 700,
                  justifyContent: 'flex-start',
                  maxWidth: '100%',
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              />
              <Chip
                size="small"
                label={smartScoreEnabled
                  ? `Progresso: ${formatPercent(smartScoreTargetProgressPercent)} da meta`
                  : 'Sem percentual'}
                variant="outlined"
                sx={{
                  fontWeight: 700,
                  justifyContent: 'flex-start',
                  maxWidth: '100%',
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Media ponderada de {smartScoreGlobal.count || 0} publicacoes com SmartScore,
              de {totalArticles.toLocaleString('pt-BR')} publicacoes no periodo.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Cobertura: {Number(smartScoreGlobal.coveragePercent || 0).toLocaleString('pt-BR')}%
              {' · '}{Number(smartScoreGlobal.pendingCount || 0).toLocaleString('pt-BR')} ainda sem SmartScore.
            </Typography>
          </Grid>

          <Grid item xs={12} md={8} lg={5}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1, mb: 1 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                Media global
              </Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ color: smartScoreEnabled ? smartScoreColor : 'text.disabled', lineHeight: 1 }}>
                {smartScoreEnabled ? formatSignedScore(smartScoreValue) : '--'}
              </Typography>
              <Typography variant="body2" fontWeight="bold" sx={{ color: smartScoreEnabled ? smartScoreColor : 'text.disabled' }}>
                {smartScoreEnabled ? smartScoreGlobal.status : 'Sem dados'}
              </Typography>
            </Box>
            <Box sx={{ position: 'relative', pt: 2, pb: 1, maxWidth: 760, mx: 'auto' }}>
              <Box
                sx={{
                  height: 10,
                  borderRadius: 999,
                  background: 'linear-gradient(90deg, #d32f2f 0%, #f59e0b 50%, #4caf50 100%)'
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 5,
                  left: `${smartScoreEnabled ? smartScorePercent : 50}%`,
                  transform: 'translateX(-50%)',
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  bgcolor: smartScoreEnabled ? smartScoreColor : 'grey.500',
                  border: '3px solid',
                  borderColor: 'background.paper',
                  boxShadow: 2
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: `${smartScoreTargetPercent}%`,
                  transform: 'translateX(-50%)',
                  width: 3,
                  height: 24,
                  borderRadius: 999,
                  bgcolor: 'text.primary',
                  opacity: 0.85
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Critico</Typography>
              <Typography variant="caption" color="text.secondary">Meta {formatSignedScore(smartScoreTargetValue)}</Typography>
              <Typography variant="caption" color="text.secondary">Excelente</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ display: 'block', mb: 1.2 }}>
              Distribuicao por escala
            </Typography>
            <Box
              sx={{
                height: 168,
                position: 'relative',
                bgcolor: 'action.hover',
                borderRadius: 1,
                p: 1.4
              }}
            >
              {smartScoreStatusTotal > 0 ? (
                <>
                  <Doughnut data={smartScoreStatusDonutData} options={smartScoreStatusDonutOptions} />
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                      textAlign: 'center',
                      flexDirection: 'column'
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold">
                      {smartScoreStatusTotal.toLocaleString('pt-BR')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      publicacoes
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Sem dados no periodo
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ display: 'block', mb: 1.2 }}>
              Legenda por status
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))', xl: 'repeat(9, minmax(0, 1fr))' }, gap: 0.75 }}>
              {smartScoreLegend.map((item) => {
                const itemColor = SMART_SCORE_STATUS_COLORS[item.label] || 'text.secondary';
                const itemPercent = smartScoreStatusTotal > 0 ? (Number(item.count || 0) / smartScoreStatusTotal) * 100 : 0;
                return (
                <Box
                  key={item.label}
                  sx={{
                    p: 0.9,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    textAlign: 'center',
                    border: '1px solid',
                    borderColor: smartScoreGlobal.status === item.label ? itemColor : 'transparent',
                    minWidth: 0
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ color: itemColor, lineHeight: 1 }}>
                    {Number(item.count || 0).toLocaleString('pt-BR')}
                  </Typography>
                  <Typography variant="caption" color="text.primary" sx={{ display: 'block', mt: 0.4, fontWeight: 700 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.64rem' }}>
                    {formatSmartScoreRange(item)}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: itemColor, fontSize: '0.64rem', fontWeight: 700 }}>
                    {formatPercent(itemPercent)}
                  </Typography>
                </Box>
                );
              })}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Impacto medio: {Number(smartScoreGlobal.averageImpact || 0).toFixed(1)}
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Composicao do SmartScore
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Media dos indicadores nos artigos filtrados
            </Typography>
          </Box>
          <Grid container spacing={1.5}>
            {smartScoreComponents.map((component) => {
              const componentColor = getIndicatorScoreColor(component.averageScore);
              const componentPercent = Math.max(0, Math.min(100, Number(component.averageScore || 0) * 10));
              const componentElements = Array.isArray(component.elements) ? component.elements : [];
              const componentElementTotal = componentElements.reduce((sum, element) => sum + Number(element.count || 0), 0);
              const hasElementDistribution = componentElementTotal > 0 && componentElements.length > 1;
              const componentElementChartData = {
                labels: componentElements.map(element => element.label),
                datasets: [{
                  data: componentElements.map(element => Number(element.count || 0)),
                  backgroundColor: componentElements.map((element, index) => getKpiElementColor(component.key, element, index)),
                  borderWidth: 0,
                  hoverOffset: 3
                }]
              };
              const componentElementChartOptions = {
                responsive: true,
                maintainAspectRatio: false,
                cutout: '68%',
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        const value = Number(context.raw || 0);
                        const percent = componentElementTotal > 0 ? (value / componentElementTotal) * 100 : 0;
                        return `${context.label}: ${value.toLocaleString('pt-BR')} (${formatPercent(percent)})`;
                      }
                    }
                  }
                }
              };
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={component.key}>
                  <Box
                    sx={{
                      p: 1.4,
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                      minHeight: hasElementDistribution ? 176 : 112
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.8 }}>
                      <Typography variant="body2" fontWeight="bold" noWrap>
                        {component.label}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: componentColor }}>
                        {Number(component.averageScore || 0).toFixed(1)}
                      </Typography>
                    </Box>
                    <Box sx={{ position: 'relative', height: 7, borderRadius: 999, bgcolor: 'rgba(148, 163, 184, 0.25)', overflow: 'hidden' }}>
                      <Box
                        sx={{
                          width: `${componentPercent}%`,
                          height: '100%',
                          borderRadius: 999,
                          bgcolor: componentColor
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.8 }}>
                      <Typography variant="caption" color="text.secondary">
                        participa do score: {Number(component.averageWeight || 0).toFixed(1)}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {component.count} publicacoes
                      </Typography>
                    </Box>
                    {componentElements.length > 0 && (
                      <Box sx={{ display: 'grid', gridTemplateColumns: hasElementDistribution ? 'minmax(0, 1fr) 92px' : '1fr', gap: 1, alignItems: 'center', mt: 1 }}>
                        <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', minWidth: 0 }}>
                          {componentElements.slice(0, 5).map((element, elementIndex) => {
                            const elementPercent = componentElementTotal > 0 ? (Number(element.count || 0) / componentElementTotal) * 100 : 0;
                            return (
                              <Chip
                                key={`${component.key}-${element.label}`}
                                size="small"
                                label={`${element.label}: ${formatPercent(elementPercent)}`}
                                sx={{
                                  height: 22,
                                  maxWidth: '100%',
                                  fontSize: '0.68rem',
                                  fontWeight: 700,
                                  color: getKpiElementColor(component.key, element, elementIndex),
                                  bgcolor: 'rgba(148, 163, 184, 0.16)',
                                  '& .MuiChip-label': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }
                                }}
                              />
                            );
                          })}
                          {componentElements.length > 5 && (
                            <Chip
                              size="small"
                              label={`+${componentElements.length - 5}`}
                              sx={{
                                height: 22,
                                fontSize: '0.68rem',
                                fontWeight: 700,
                                bgcolor: 'rgba(148, 163, 184, 0.16)'
                              }}
                            />
                          )}
                        </Box>
                        {hasElementDistribution && (
                          <Box sx={{ height: 86, position: 'relative', minWidth: 0 }}>
                            <Doughnut data={componentElementChartData} options={componentElementChartOptions} />
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                pointerEvents: 'none'
                              }}
                            >
                              <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                {componentElementTotal}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Diagnostico por faixa de resultado
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Fatores que mais explicam os status atingidos
            </Typography>
          </Box>
          <Grid container spacing={1.5}>
            {smartScoreLegend
              .filter(statusItem => Number(statusItem.count || 0) > 0)
              .map((statusItem) => {
                const { weak, strong } = getStatusInsightItems(statusItem);
                const statusColor = SMART_SCORE_STATUS_COLORS[statusItem.label] || 'text.secondary';

                return (
                  <Grid item xs={12} md={6} lg={4} key={`insight-${statusItem.label}`}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                        border: '1px solid',
                        borderColor: 'divider',
                        minHeight: 170
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                        <Box>
                          <Typography variant="body2" fontWeight="bold" sx={{ color: statusColor }}>
                            {statusItem.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatSmartScoreRange(statusItem)}
                          </Typography>
                        </Box>
                        <Typography variant="h6" fontWeight="bold" sx={{ color: statusColor, lineHeight: 1 }}>
                          {Number(statusItem.count || 0).toLocaleString('pt-BR')}
                        </Typography>
                      </Box>

                      <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        Pontos que seguraram
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.7, mb: 1.2 }}>
                        {(weak.length ? weak : [{ label: 'Sem gargalo claro', averageScore: null }]).map((component) => (
                          <Chip
                            key={`weak-${statusItem.label}-${component.label}`}
                            size="small"
                            label={component.averageScore === null ? component.label : `${component.label} ${Number(component.averageScore || 0).toFixed(1)}`}
                            sx={{
                              height: 24,
                              fontWeight: 700,
                              color: component.averageScore === null ? 'text.secondary' : getIndicatorScoreColor(component.averageScore),
                              bgcolor: 'rgba(148, 163, 184, 0.16)'
                            }}
                          />
                        ))}
                      </Box>

                      <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        Pontos fortes
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.7 }}>
                        {(strong.length ? strong : [{ label: 'Sem destaque claro', averageScore: null }]).map((component) => (
                          <Chip
                            key={`strong-${statusItem.label}-${component.label}`}
                            size="small"
                            label={component.averageScore === null ? component.label : `${component.label} ${Number(component.averageScore || 0).toFixed(1)}`}
                            sx={{
                              height: 24,
                              fontWeight: 700,
                              color: component.averageScore === null ? 'text.secondary' : getIndicatorScoreColor(component.averageScore),
                              bgcolor: 'rgba(148, 163, 184, 0.16)'
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
          </Grid>
        </Box>
      </Paper>
      </>
      )}

      {dashboardLayer === 'performance' && (
      <>
      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {kpiCards.map((kpi, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ color: kpi.color, mr: 2 }}>
                    {kpi.icon}
                  </Box>
                </Box>
                {kpi.breakdown ? (
                  <Box sx={{ display: 'grid', gap: 0.5, mb: 1 }}>
                    {kpi.breakdown.map(item => (
                      <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: item.color, fontWeight: 700 }}>
                          {item.label}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {item.format === 'currencyBrl' ? `R$ ${item.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : item.format === 'currencyUsd' ? `US$ ${item.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : item.value.toLocaleString('pt-BR')}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="h5" fontWeight="bold" sx={{ mb: 1, fontSize: '1.25rem' }}>
                    {kpi.value}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  {kpi.title}
                </Typography>
                {kpi.helper && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {kpi.helper}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      </>
      )}

      {dashboardLayer === 'editorial' && (
      <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Base do periodo: {totalArticles.toLocaleString('pt-BR')} publicacoes
        {' · '}Exposicao classificada: {Number(metricCoverage.exposure || 0).toLocaleString('pt-BR')}
        {' · '}Abrangencia classificada: {Number(metricCoverage.scope || 0).toLocaleString('pt-BR')}
      </Typography>
      {/* Share of Voice Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Share of Voice ({activeBrandData?.name || 'Marca'} vs Concorrentes)
              </Typography>
              <Box sx={{ height: 300 }}>
                <Pie data={sovPieChartData} options={{ ...chartOptions, maintainAspectRatio: false }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Comparativo ({activeBrandData?.name || 'Marca'} x Cada Concorrente)
              </Typography>
              <Box sx={{ height: 300 }}>
                <Bar data={sovBarChartData} options={{ ...chartOptions, maintainAspectRatio: false }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </>
      )}

      {dashboardLayer === 'performance' && (
      <>
      {/* New Charts: Platforms & Category & Scope & UF */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Plataformas
              </Typography>
              <Box sx={{ height: 250 }}>
                <Bar data={platformChartData} options={chartOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Categorias
              </Typography>
              <Box sx={{ height: 250 }}>
                <Pie data={categoryChartData} options={chartOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Abrang�ncia
              </Typography>
              <Box sx={{ height: 250 }}>
                <Pie data={scopeChartData} options={chartOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Top Estados ({Number(metricCoverage.uf || 0).toLocaleString('pt-BR')} de {totalArticles.toLocaleString('pt-BR')} classificados)
              </Typography>
              <Box sx={{ height: 250 }}>
                <Bar data={ufChartData} options={chartOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </>
      )}

      {dashboardLayer === 'performance' && (
      <>
      {/* Charts Row 1 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Articles Over Time */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Artigos ao Longo do Tempo
              </Typography>
              <Box sx={{ height: 300 }}>
                <Line data={articlesOverTimeData} options={chartOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Sentiment Distribution */}
        <Grid item xs={12} md={6} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Distribuição de Sentimento
              </Typography>
              <Box sx={{ height: 300 }}>
                <Pie data={sentimentData} options={chartOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </>
      )}

      {dashboardLayer === 'editorial' && (
      <>
      {/* Charts Row 2 */}
      <Grid container spacing={3}>
        {/* Word Cloud */}
        <Grid item xs={12} lg={8}>
          <WordCloudCard
            wordCloud={wordCloud}
            tagStats={tagStats}
            keywordsCloud={keywordsCloud}
            wordLimit={wordLimit}
            setWordLimit={setWordLimit}
            cloudType={cloudType}
            setCloudType={setCloudType}
            contextFilter={wordCloudContext}
            setContextFilter={setWordCloudContext}
            loading={cloudType === 'words' ? loadingWords : loadingKeywordsCloud}
          />
        </Grid>

        {/* Exposure Distribution */}
        <Grid item xs={12} md={6} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Distribuição de Exposição
              </Typography>
              <Box sx={{ height: 300 }}>
                <Pie data={exposureData} options={chartOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </>
      )}

      {dashboardLayer === 'performance' && (
      <>
      {/* Top Collections */}
      {
        collectionStats && collectionStats.length > 0 && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Top Coleções
              </Typography>
              <Grid container spacing={2}>
                {collectionStats.slice(0, 10).map((col, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body1">{col.name}</Typography>
                      <Chip label={col.count} color="primary" size="small" />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )
      }
      </>
      )}

      {dashboardLayer === 'pr-results' && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2
          }}
        >
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Resultados de PR
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Lamina preparada para consolidar os resultados do trabalho da assessoria no periodo selecionado.
            {' '}O periodo contem {totalArticles.toLocaleString('pt-BR')} publicacoes; os numeros abaixo sao registros de PR independentes.
          </Typography>
          <Grid container spacing={2}>
            {[
              { label: 'Temas Trabalhados', value: prResults.themesWorked || 0 },
              { label: 'Conteudos Divulgados', value: prResults.contentsPublished || 0 },
              { label: 'Atendimentos / Acoes', value: prResults.actionsPerformed || 0 },
              { label: 'Tipos de Acao', value: prResults.byAction?.length || 0 },
              { label: 'Status Registrados', value: prResults.byStatus?.length || 0 },
              { label: 'Sumario Executivo', value: 'Em preparacao' },
            ].map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.label}>
                <Box sx={{ p: 2, borderRadius: 1, bgcolor: 'action.hover', minHeight: 92 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">
                    {item.label}
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }}>
                    {item.value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Acoes por tipo
                </Typography>
                {(prResults.byAction || []).slice(0, 6).map((item) => (
                  <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                    <Typography variant="body2" fontWeight="bold">{item.count}</Typography>
                  </Box>
                ))}
                {!(prResults.byAction || []).length && (
                  <Typography variant="body2" color="text.secondary">Nenhuma acao cadastrada no periodo.</Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Status dos atendimentos
                </Typography>
                {(prResults.byStatus || []).slice(0, 6).map((item) => (
                  <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                    <Typography variant="body2" fontWeight="bold">{item.count}</Typography>
                  </Box>
                ))}
                {!(prResults.byStatus || []).length && (
                  <Typography variant="body2" color="text.secondary">Nenhum status cadastrado no periodo.</Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box >
  );
};

export default Dashboard;
